import { lazy, Suspense, useCallback, useEffect, useState } from 'react'

import { IdeLayout } from '@/layouts/IdeLayout'
import { useAuth } from '@/hooks/useAuth'
import { useIdeStore } from '@/store/ideStore'
import { workspaceService } from '@/services/workspaceService'
import { aiService } from '@/services/aiService'
import { ideApi } from '@/api/ideApi'
import { useSessionHeartbeat } from '@/hooks/useSessionHeartbeat'
import { NewProjectFlow } from '@/components/projects/NewProjectFlow'
import { CommandPalette } from '@/components/editor/CommandPalette'
import { GovernedRunnerPanel } from '@/components/runner/GovernedRunnerPanel'

const CodeEditor = lazy(() => import('@/components/editor/CodeEditor').then(module => ({ default: module.CodeEditor })))
const TerminalPanel = lazy(() => import('@/components/terminal/TerminalPanel').then(module => ({ default: module.TerminalPanel })))
const AIAssistantPanel = lazy(() => import('@/components/ai/AIAssistantPanel').then(module => ({ default: module.AIAssistantPanel })))

function FallbackPanel({ label }) {
  return <div className="panel-loader">Loading {label}...</div>
}

export function IdePage() {
  const { session, logout, refreshSession } = useAuth()
  useSessionHeartbeat(session.authenticated, session.governedRuntime)
  const [createFlowOpen, setCreateFlowOpen] = useState(false)
  const [creatingProject, setCreatingProject] = useState(false)
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)

  const {
    currentProject,
    openFiles,
    activeFilePath,
    aiMessages,
    usageSnapshot,
    upgradePrompt,
    setUpgradePrompt,
    setRecentProjects,
    setActiveFile,
    closeFile,
    updateFileContents,
    appendTerminalEntry,
  } = useIdeStore()

  useEffect(() => {
    setRecentProjects(session.recentProjects || [])
  }, [session.recentProjects, setRecentProjects])

  useEffect(() => {
    if (!window.arkitectDesktop || !window.arkitectDesktop.auth?.onBrowserSuccess) return

    const unsubscribe = window.arkitectDesktop.auth.onBrowserSuccess(async () => {
      try {
        await refreshSession()
      } catch (err) {
        console.error('Failed to load session after browser login:', err)
      }
    })
    return unsubscribe
  }, [refreshSession])

  const onLogin = async () => {
    try {
      if (window.arkitectDesktop?.auth?.openBrowserLogin) {
        await window.arkitectDesktop.auth.openBrowserLogin()
      } else {
        console.warn('Browser login only available in Electron')
      }
    } catch (error) {
      console.error('Unable to open browser login:', error)
    }
  }

  const activeFile = openFiles.find(file => file.path === activeFilePath) || null

  const onCreateProject = async () => {
    setCreateFlowOpen(true)
  }

  const onCreateProjectSubmit = async ({ name, visibility }) => {
    if (!name?.trim()) {
      return
    }
    try {
      setCreatingProject(true)
      const project = await workspaceService.createLocalProject({
        name,
        visibility,
        syncWithBackend: Boolean(session?.authenticated),
      })
      setCreateFlowOpen(false)
      if (session?.authenticated && project?.syncError) {
        setUpgradePrompt(`Project created locally. Backend sync failed: ${project.syncError}`)
      } else {
        setUpgradePrompt(null)
      }
    } catch (error) {
      setUpgradePrompt(String(error?.message || 'Unable to create project.'))
    } finally {
      setCreatingProject(false)
    }
  }

  const onGenerateBlueprint = async ({ projectName, requirement, stack, priority, auth, aiAgent, mode = 'blueprint' }) => {
    if (!session?.authenticated) {
      throw new Error('Sign in to generate AI blueprint recommendations.')
    }

    const prompt =
      mode === 'requirements'
        ? [
            `Project name: ${projectName || 'Untitled project'}`,
            `Idea input: ${requirement || 'No requirement provided.'}`,
            'Turn this into a clean product requirements document.',
            'Include:',
            '1) Objective',
            '2) User stories',
            '3) Functional requirements',
            '4) Non-functional requirements',
            '5) Milestones',
          ].join('\n')
        : [
            `Project name: ${projectName || 'Untitled project'}`,
            `Primary requirement: ${requirement || 'No requirement provided.'}`,
            `Preferred stack: ${stack}`,
            `Delivery priority: ${priority}`,
            `Auth strategy: ${auth}`,
            'Return a concise architecture blueprint with:',
            '1) Core modules',
            '2) Data flow',
            '3) Suggested folder structure',
            '4) Immediate implementation steps',
          ].join('\n')

    const response = await ideApi.generateAi({
      prompt,
      task_type: 'documentation',
      ai_agent: String(aiAgent || 'claude').toLowerCase(),
    })

    return response?.data?.output || ''
  }

  const onOpenProject = async () => {
    try {
      await workspaceService.openLocalProject()
    } catch (error) {
      setUpgradePrompt(String(error?.message || 'Unable to open project.'))
    }
  }

  const onOpenTreeFile = async node => {
    await workspaceService.openFile(node)
  }

  const onSaveFile = useCallback(async () => {
    try {
      await workspaceService.saveActiveFile()
    } catch (error) {
      setUpgradePrompt(String(error?.message || 'Unable to save file.'))
    }
  }, [setUpgradePrompt])

  const onSendPrompt = async (prompt, taskType = 'chat', aiAgent = 'claude') => {
    if (!session.authenticated) {
       setUpgradePrompt('You must be logged in to use Arkitect AI features.')
       return
    }
    if (!prompt.trim()) {
      return
    }
    try {
      await aiService.sendPrompt(prompt, taskType, aiAgent)
    } catch (error) {
      appendTerminalEntry({
        command: 'ai.generate',
        stdout: '',
        stderr: String(error?.message || 'AI request failed.'),
      })
    }
  }

  // ── Command Palette Shortcut ────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault()
        setCommandPaletteOpen(v => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  const onCommandAction = useCallback((action) => {
    switch (action) {
      case 'save': onSaveFile(); break
      case 'openProject': onOpenProject(); break
      case 'createProject': onCreateProject(); break
      case 'logout': logout(); break
      case 'resetSetup':
        localStorage.removeItem('arkitect_setup_completed')
        window.location.reload()
        break
      default: break
    }
  }, [onSaveFile, logout, onOpenProject, onCreateProject])

  return (
    <>
      <IdeLayout
        session={session}
        governedRuntime={session.governedRuntime}
        project={currentProject}
        files={openFiles}
        activeFile={activeFile}
        onSelectFile={setActiveFile}
        onCloseFile={closeFile}
        onOpenTreeFile={onOpenTreeFile}
        onCreateProject={onCreateProject}
        onOpenProject={onOpenProject}
        onSaveFile={onSaveFile}
        onLogin={onLogin}
        onLogout={logout}
        editor={
          <Suspense fallback={<FallbackPanel label="editor" />}>
            <CodeEditor
              file={activeFile}
              onChange={contents => {
                if (activeFile) {
                  updateFileContents(activeFile.path, contents)
                }
              }}
            />
          </Suspense>
        }
        assistant={
          <Suspense fallback={<FallbackPanel label="assistant" />}>
            <AIAssistantPanel
              messages={aiMessages}
              upgradePrompt={upgradePrompt}
              usageSnapshot={usageSnapshot || session.usage}
              onSendPrompt={onSendPrompt}
            />
          </Suspense>
        }
        terminal={
          <Suspense fallback={<FallbackPanel label="terminal" />}>
            {session.governedRuntime ? (
              <GovernedRunnerPanel runtime={session.governedRuntime} />
            ) : (
              <TerminalPanel cwd={currentProject?.path} />
            )}
          </Suspense>
        }
      />
      <NewProjectFlow
        open={createFlowOpen}
        creating={creatingProject}
        error={upgradePrompt}
        onClose={() => setCreateFlowOpen(false)}
        onCreate={onCreateProjectSubmit}
        onGenerateBlueprint={onGenerateBlueprint}
      />
      <CommandPalette
        open={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        onAction={onCommandAction}
      />
    </>
  )
}
